/**
 * Pure domain logic for the Goods Sale (Vanzare Marfa) page.
 *
 * The heavy lifting (querying stock, applying the markup) happens on the backend;
 * these helpers cover the client-side formatting, validation and selection rules
 * so the React component stays free of business logic.
 */

/** Keywords (lowercase, Romanian) that identify a precious-metal object. */
export const PRECIOUS_METAL_KEYWORDS = ['argint', 'aur', 'platina', 'paladiu'] as const;

/** Minimum number of objects that must be selected to issue a goods-sale invoice. */
export const MIN_SELL_ITEMS = 1;

/** A goods item as returned by the `/api/goods-sale/items` endpoint. */
export interface GoodsSaleItem {
    id: number;
    nrContract: number;
    contractNumber: string | null;
    data: string | null;
    denumire: string | null;
    cantitate: number | null;
    um: string | null;
    sumainLei: number;
    propus: number;
    status: string | null;
    invFizic: boolean;
}

/** A single object being sold. */
export interface SellItem {
    nrContract: number;
    denumire: string;
    suma: number;
}

/** A single stock-take row from the `/api/goods-sale/physical-inventory` endpoint. */
export interface PhysicalInventoryItem {
    id: number;
    nr: number | null;
    nrContract: string | null;
    denumire: string | null;
    um: string | null;
    cantitate: number | null;
    sumainLei: number | null;
    inv: boolean;
    invFizic: boolean;
    contabilitate: boolean;
    mentiuni: string | null;
}

/**
 * Row background colour for the physical inventory grid, mirroring the legacy
 * `smdbgStockMarfuriDrawColumnCell` handler: rows start highlighted as pending
 * (yellow) and turn green once both the physical check and the accounting
 * confirmation are recorded.
 */
export function getPhysicalInventoryRowColor(
    item: Pick<PhysicalInventoryItem, 'invFizic' | 'contabilitate'>,
): string {
    return item.invFizic && item.contabilitate ? '#d9f7be' : '#fffbe6';
}

/**
 * Determine whether an object description refers to a precious metal.
 */
export function isPreciousMetal(description: string | null | undefined): boolean {
    if (!description) {
        return false;
    }
    const lower = description.toLowerCase();
    return PRECIOUS_METAL_KEYWORDS.some(keyword => lower.includes(keyword));
}

/**
 * Format a currency value using two decimals, as required by the display rules.
 * Non-finite values render as `0.00`.
 */
export function formatLei(value: number | null | undefined): string {
    const numeric = Number(value);
    const safe = Number.isFinite(numeric) ? numeric : 0;
    return safe.toFixed(2);
}

/**
 * Format a sale date for display, keeping only the calendar day in
 * `YYYY-MM-DD` form. Accepts either a plain date string or a full datetime
 * (ISO or `YYYY-MM-DD HH:mm:ss`) and returns an empty string for missing or
 * unparseable input.
 */
export function formatSaleDate(value: string | null | undefined): string {
    if (!value) {
        return '';
    }
    const match = /^\d{4}-\d{2}-\d{2}/.exec(value.trim());
    if (match) {
        return match[0];
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
        return '';
    }
    const year = parsed.getFullYear();
    const month = String(parsed.getMonth() + 1).padStart(2, '0');
    const day = String(parsed.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

/**
 * Normalise a markup percentage entered by the user: clamp to a non-negative
 * number, falling back to `0` for invalid input.
 */
export function normaliseMarkup(value: number | null | undefined): number {
    const numeric = Number(value);
    if (!Number.isFinite(numeric) || numeric < 0) {
        return 0;
    }
    return numeric;
}

/**
 * Recompute a proposed price for a paid amount and markup percentage, mirroring
 * the backend formula `round(paid * (1 + markup / 100), 2)`.
 */
export function computeProposedPrice(
    paidAmount: number | null | undefined,
    markupPercent: number | null | undefined,
): number {
    const numericPaid = Number(paidAmount);
    const paid = Number.isFinite(numericPaid) ? numericPaid : 0;
    const markup = normaliseMarkup(markupPercent);
    return Math.round(paid * (1 + markup / 100) * 100) / 100;
}

/**
 * Whether the current selection can be sold (at least one object).
 */
export function canSellSelection(selectedCount: number): boolean {
    return selectedCount >= MIN_SELL_ITEMS;
}

/**
 * Whether an individual goods item may be sold. Items that are not physically
 * present in stock (`invFizic` is falsy) cannot be sold, so their selection
 * checkbox is disabled in the sale grid.
 */
export function canSellItem(item: Pick<GoodsSaleItem, 'invFizic'>): boolean {
    return Boolean(item.invFizic);
}

/** Maximum length of the concatenated contract label before it is collapsed to `LIST`. */
export const MAX_CONTRACT_LABEL_LENGTH = 100;

/**
 * Build the contract label for a sale from the selected rows by joining the
 * distinct contract numbers with `+`. When the concatenated label exceeds
 * {@link MAX_CONTRACT_LABEL_LENGTH} characters it is collapsed to `LIST`, since
 * the sale would otherwise fail on the overly long value.
 */
export function buildContractLabel(rows: GoodsSaleItem[]): string {
    const joined = Array.from(
        new Set(rows.map(row => row.contractNumber ?? String(row.nrContract))),
    ).join('+');
    return joined.length > MAX_CONTRACT_LABEL_LENGTH ? 'LIST' : joined;
}

/**
 * Build the `items` payload for the sell endpoint from the selected rows.
 */
export function buildSellItems(rows: GoodsSaleItem[]): SellItem[] {
    return rows.map(row => ({
        nrContract: row.nrContract,
        denumire: row.denumire ?? '',
        suma: row.propus,
    }));
}

/** A goods item as it appears in the sell price-entry step. */
export interface SaleModalItem {
    id: number;
    nrContract: number;
    contractNumber: string | null;
    denumire: string | null;
    sumainLei: number;
    propus: number;
    /** The operator-confirmed sale price; initially pre-filled with `propus`. */
    salePrice: number;
}

/**
 * Build the initial list of sale modal items from the selected grid rows,
 * pre-filling each sale price with the item's proposed price.
 */
export function buildSaleModalItems(rows: GoodsSaleItem[]): SaleModalItem[] {
    return rows.map(row => ({
        id: row.id,
        nrContract: row.nrContract,
        contractNumber: row.contractNumber,
        denumire: row.denumire,
        sumainLei: row.sumainLei,
        propus: row.propus,
        salePrice: row.propus,
    }));
}

/**
 * Build the `items` payload for the sell endpoint from the sell modal items,
 * using the operator-confirmed sale price for each item.
 */
export function buildSellItemsFromModal(items: SaleModalItem[]): SellItem[] {
    return items.map(item => ({
        nrContract: item.nrContract,
        denumire: item.denumire ?? '',
        suma: item.salePrice,
    }));
}
